import { useEffect, useState } from "react";
import { LayoutDashboard, ShoppingCart, FileText, Store, UtensilsCrossed, ClipboardList, Wallet, Scale } from "lucide-react";

const SYSTEMS = {
  profesionalpos: {
    label: "ProfesionalPOS", icon: Store,
    shots: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, src: "/img/pos/dashboard.webp", desc: "Ventas del día y del mes, liquidez, utilidad y salud financiera en tiempo real, con un director financiero virtual." },
      { id: "punto-de-venta", label: "Punto de Venta", icon: ShoppingCart, src: "/img/pos/punto-de-venta.webp", desc: "Cobra rápido con catálogo visual, búsqueda por código o SKU, categorías y ticket en un clic." },
      { id: "facturacion", label: "Facturación CFDI", icon: FileText, src: "/img/pos/facturacion.webp", desc: "Compra timbres, emite y descarga tus CFDI 4.0 desde el mismo sistema." },
    ],
  },
  profesionalpos_restaurante: {
    label: "ProfesionalPOS Restaurante", icon: UtensilsCrossed,
    shots: [
      { id: "r-dashboard", label: "Dashboard", icon: LayoutDashboard, src: "/img/rest/dashboard.webp", desc: "Ventas, utilidad, ticket promedio, platillos más vendidos y un director virtual con IA para tu restaurante." },
      { id: "r-pedido", label: "Generar pedido", icon: ClipboardList, src: "/img/rest/pedido.webp", desc: "Pedido en mesa, venta rápida, llamada, WhatsApp, Uber Eats, DiDi Food y Rappi desde una sola pantalla." },
      { id: "r-caja", label: "Mi Caja", icon: Wallet, src: "/img/rest/caja.webp", desc: "Efectivo, tarjeta, transferencias y fondo de propinas con todos los movimientos de tu turno." },
      { id: "r-conciliacion", label: "Conciliación de plataformas", icon: Scale, src: "/img/rest/conciliacion.webp", desc: "Cuánto te deben las apps: comisiones, marketing, impuestos y neto estimado por plataforma." },
    ],
  },
};
const ROTATE_MS = 6000;

function Monitor({ shots, active }) {
  return (
    <div className="mx-auto w-full max-w-4xl" data-testid="showcase-monitor">
      <div className="rounded-[1.25rem] bg-[#0B1220] p-2.5 shadow-[0_40px_80px_-30px_rgba(10,30,74,0.55)] sm:p-3.5">
        <div className="relative aspect-[1723/1364] overflow-hidden rounded-lg bg-white">
          {shots.map((s) => (
            <img key={s.id} src={s.src} alt={`Captura · ${s.label}`} loading="lazy" decoding="async"
              className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${s.id === active ? "opacity-100" : "opacity-0"}`} />
          ))}
        </div>
      </div>
      <div className="mx-auto h-10 w-24 bg-gradient-to-b from-[#1a2336] to-[#0B1220] [clip-path:polygon(18%_0,82%_0,100%_100%,0_100%)]" />
      <div className="mx-auto h-2.5 w-56 rounded-full bg-[#0B1220]" />
    </div>
  );
}

function SystemToggle({ value, onChange }) {
  return (
    <div className="mt-6 inline-flex rounded-full border border-border bg-card p-1" data-testid="showcase-system-toggle">
      {Object.entries(SYSTEMS).map(([id, s]) => (
        <button key={id} onClick={() => onChange(id)} data-testid={`showcase-system-${id}`}
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 sm:text-sm ${value === id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          <s.icon className="h-4 w-4" />{s.label}
        </button>
      ))}
    </div>
  );
}

export function ProductShowcase() {
  const [system, setSystem] = useState("profesionalpos");
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const shots = SYSTEMS[system].shots;
  useEffect(() => {
    if (paused) { return undefined; }
    const t = setInterval(() => setIdx((i) => (i + 1) % shots.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused, shots.length]);
  const pick = (id) => { setSystem(id); setIdx(0); };
  return (
    <section id="conoce" className="bg-secondary/50" data-testid="product-showcase">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-[1fr_2fr] lg:items-center">
          <div>
            <p className="overline text-primary">Conoce los sistemas por dentro</p>
            <h2 className="mt-3 font-display text-4xl font-extrabold tracking-tight md:text-5xl">Todo tu negocio en una sola pantalla</h2>
            <SystemToggle value={system} onChange={pick} />
            <div className="mt-6 space-y-3" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
              {shots.map((s, i) => (
                <button key={s.id} onClick={() => setIdx(i)} data-testid={`showcase-tab-${s.id}`}
                  className={`w-full rounded-xl border p-4 text-left transition-[background-color,border-color,transform] duration-300 ${i === idx ? "border-primary bg-card shadow-sm" : "border-transparent hover:translate-x-1 hover:bg-card/60"}`}>
                  <p className={`flex items-center gap-2 font-semibold ${i === idx ? "text-primary" : "text-foreground"}`}><s.icon className="h-4 w-4" />{s.label}</p>
                  {i === idx && <p className="mt-1.5 text-sm text-muted-foreground animate-fade-up">{s.desc}</p>}
                </button>
              ))}
            </div>
          </div>
          <Monitor shots={shots} active={shots[idx % shots.length].id} />
        </div>
      </div>
    </section>
  );
}
